<template>
  <el-form ref="form" :model="localData" :rules="rules" label-width="160px" size="small">
    <el-divider>结算账户信息</el-divider>

    <el-form-item label="账户类型" prop="bankAccountType">
      <el-select v-model="localData.bankAccountType" placeholder="请选择账户类型" style="width:100%" @change="handleAccountTypeChange">
        <el-option v-for="item in accountTypeOptions" :key="item.value" :label="item.label" :value="accountTypeMap[item.value]" />
      </el-select>
    </el-form-item>

    <el-form-item label="开户名称" prop="accountName">
      <el-input v-model="localData.accountName" :placeholder="accountPlaceholder" />
    </el-form-item>

    <el-form-item label="开户银行" prop="accountBank">
      <el-select v-model="selectedBankCode" placeholder="请选择开户银行" style="width:100%" filterable :loading="bankLoading" @change="handleBankChange">
        <el-option v-for="bank in bankList" :key="bank.bank_alias_code" :label="bank.bank_alias" :value="bank.bank_alias_code" />
      </el-select>
    </el-form-item>

    <template v-if="needBranchQuery">
      <el-form-item label="开户省份" prop="provinceCode">
        <el-select v-model="provinceCode" placeholder="请选择开户省份" style="width:100%" filterable :loading="provinceLoading" @change="handleProvinceChange">
          <el-option v-for="p in provinceList" :key="p.value" :label="p.label" :value="p.value" />
        </el-select>
      </el-form-item>

      <el-form-item label="开户城市" prop="wxCityCode">
        <el-select v-model="wxCityCode" placeholder="请先选择开户省份" style="width:100%" filterable :loading="cityLoading" :disabled="!provinceCode" @change="handleWxCityChange">
          <el-option v-for="c in wxCityList" :key="c.value" :label="c.label" :value="c.value" />
        </el-select>
      </el-form-item>

      <el-form-item label="开户支行" prop="bankName">
        <el-select v-model="localData.bankName" placeholder="请先选择开户城市" style="width:100%" filterable :loading="branchLoading" :disabled="branchList.length === 0" @change="handleBranchChange">
          <el-option v-for="b in branchList" :key="b.bank_branch_id" :label="b.bank_branch_name" :value="b.bank_branch_id" />
        </el-select>
      </el-form-item>

      <el-form-item label="联行号" prop="bankBranchId">
        <el-input v-model="localData.bankBranchId" placeholder="自动填充" disabled />
      </el-form-item>
    </template>

    <el-form-item label="银行账号" prop="accountNumber">
      <el-input v-model="localData.accountNumber" placeholder="请输入银行账号" />
    </el-form-item>

    <div class="module-footer">
      <el-button type="primary" @click="submitModule" :loading="saving">保 存</el-button>
    </div>
  </el-form>
</template>

<script>
import api from '@/utils/api'
import { accountTypeOptions, accountTypeMap, accountPlaceholderMap } from '@/utils/merchantCategoryOptions'

export default {
  name: 'SettlementInfo',
  props: {
    data: { type: Object, default: () => ({}) },
    applyId: { type: Number, default: null },
    applyNo: { type: String, default: '' },
    merchantId: { type: Number, default: null }
  },
  data() {
    return {
      saving: false,
      accountTypeOptions, accountTypeMap,
      selectedBankCode: '',
      localData: {
        bankAccountType: '',
        accountName: '',
        accountBank: '',        // 存银行名称（微信要求）
        accountBankCode: '',    // 存银行编码（查询支行用）
        bankAddressCode: '',
        bankBranchId: '',
        bankName: '',
        accountNumber: ''
      },
      bankList: [], bankLoading: false,
      provinceList: [], provinceLoading: false,
      wxCityList: [], cityLoading: false,
      branchList: [], branchLoading: false,
      provinceCode: null, wxCityCode: null,
      rules: {
        bankAccountType: [{ required: true, message: '请选择账户类型', trigger: 'change' }],
        accountName: [{ required: true, message: '请输入开户名称', trigger: 'blur' }],
        accountBank: [{ required: true, message: '请选择开户银行', trigger: 'change' }],
        accountNumber: [
          { required: true, message: '请输入银行账号', trigger: 'blur' },
          { validator: (r, v, cb) => { if (!v) cb(new Error('请输入银行账号')); else if (!/^\d+$/.test(v)) cb(new Error('只能包含数字')); else cb() }, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    accountPlaceholder() {
      const t = Object.keys(accountTypeMap).find(k => accountTypeMap[k] === this.localData.bankAccountType)
      return accountPlaceholderMap[t] || '请输入开户名称'
    },
    selectedBank() { return this.bankList.find(b => b.bank_alias_code === this.selectedBankCode) },
    needBranchQuery() { return this.selectedBank?.need_bank_branch === true },
    accountTypeParam() {
      return this.localData.bankAccountType === 'BANK_ACCOUNT_TYPE_PERSONAL' ? 'PERSONAL' : 'CORPORATE'
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.localData = {
            bankAccountType: val.bankAccountType !== undefined ? val.bankAccountType : this.localData.bankAccountType,
            accountName: val.accountName !== undefined ? val.accountName : this.localData.accountName,
            accountBank: val.accountBank !== undefined ? val.accountBank : this.localData.accountBank,
            accountBankCode: val.accountBankCode !== undefined ? val.accountBankCode : this.localData.accountBankCode,
            bankAddressCode: val.bankAddressCode !== undefined ? val.bankAddressCode : this.localData.bankAddressCode,
            bankBranchId: val.bankBranchId !== undefined ? val.bankBranchId : this.localData.bankBranchId,
            bankName: val.bankName !== undefined ? val.bankName : this.localData.bankName,
            accountNumber: val.accountNumber !== undefined ? val.accountNumber : this.localData.accountNumber
          }
          if (val.accountBankCode) {
            this.selectedBankCode = val.accountBankCode
          }
        }
      }
    },
    'localData.bankAccountType'() { this.resetForm(); this.loadBankList() }
  },
  created() { this.loadBankList(); this.loadProvinces() },
  methods: {
    triggerUpload(f) { this.$emit('upload', f) },
    validate(cb) { if (this.$refs.form) this.$refs.form.validate(cb); else cb(false) },

    async loadProvinces() {
      this.provinceLoading = true
      try {
        const res = await api.subMerchant_getProvinces({})
        if (res?.data?.success && res.data.data) {
          const provinces = res.data.data.provinces || res.data.data.data || []
          this.provinceList = provinces.map(p => ({ value: p.province_code || p.value, label: p.province_name || p.label }))
        }
      } catch (e) { console.error('加载省份失败:', e) }
      finally { this.provinceLoading = false }
    },

    async handleProvinceChange(code) {
      this.wxCityCode = null; this.wxCityList = []
      this.localData.bankName = ''; this.localData.bankBranchId = ''
      this.branchList = []
      if (!code) return
      this.cityLoading = true
      try {
        const res = await api.subMerchant_getCities({ provinceCode: code })
        if (res?.data?.success && res.data.data) {
          const cities = res.data.data.cities || res.data.data.data || []
          this.wxCityList = cities.map(c => ({ value: c.city_code || c.value, label: c.city_name || c.label }))
        }
      } catch (e) { console.error('加载城市失败:', e) }
      finally { this.cityLoading = false }
    },

    handleWxCityChange(code) {
      this.localData.bankName = ''; this.localData.bankBranchId = ''
      this.branchList = []
      this.localData.bankAddressCode = code
      if (code) this.queryBranches()
    },

    async loadBankList() {
      this.bankLoading = true
      try {
        const res = await api.subMerchant_getBankList({ accountType: this.accountTypeParam, offset: 0, limit: 200 })
        if (res?.data?.success) {
          this.bankList = res.data.data.banks || []
          // 根据 accountBank 名称反查编码
          if (this.localData.accountBank && !this.localData.accountBankCode) {
            const bank = this.bankList.find(b => b.bank_alias === this.localData.accountBank)
            if (bank) {
              this.selectedBankCode = bank.bank_alias_code
              this.localData.accountBankCode = bank.bank_alias_code
            }
          }
        }
      } catch (e) { console.error('加载银行列表失败:', e) }
      finally { this.bankLoading = false }
    },

    resetForm() {
      this.selectedBankCode = ''
      this.localData.accountBank = ''
      this.localData.accountBankCode = ''
      this.localData.bankName = ''
      this.localData.bankBranchId = ''
      this.localData.bankAddressCode = ''
      this.provinceCode = null; this.wxCityCode = null
      this.wxCityList = []; this.branchList = []
    },

    handleAccountTypeChange() { this.resetForm(); this.loadBankList() },

    handleBankChange(code) {
      const bank = this.bankList.find(b => b.bank_alias_code === code)
      if (bank) {
        // accountBank 存银行名称（微信要求）
        this.localData.accountBank = bank.bank_alias
        // accountBankCode 存编码（查询支行用）
        this.localData.accountBankCode = bank.bank_alias_code
      }
      this.localData.bankName = ''; this.localData.bankBranchId = ''
      this.localData.bankAddressCode = ''
      this.provinceCode = null; this.wxCityCode = null
      this.wxCityList = []; this.branchList = []
      if (this.needBranchQuery) {
        this.rules.provinceCode = [{ required: true, message: '请选择开户省份', trigger: 'change' }]
        this.rules.wxCityCode = [{ required: true, message: '请选择开户城市', trigger: 'change' }]
        this.rules.bankName = [{ required: true, message: '请选择开户支行', trigger: 'change' }]
      }
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    },

    handleBranchChange(branchId) {
      const b = this.branchList.find(x => x.bank_branch_id === branchId)
      if (b) {
        this.localData.bankName = b.bank_branch_name
        this.localData.bankBranchId = b.bank_branch_id
      }
    },

    async queryBranches() {
      const aliasCode = this.localData.accountBankCode
      const code = this.localData.bankAddressCode
      if (!aliasCode || !code) return
      this.branchLoading = true
      try {
        const res = await api.subMerchant_getBankBranches({ bankAliasCode: aliasCode, cityCode: code, offset: 0, limit: 100 })
        if (res?.data?.success && res.data.data) {
          this.branchList = res.data.data.branches || []
          if (this.branchList.length === 0) this.$message.warning('该地区未查询到支行信息')
        }
      } catch (e) { console.error('查询支行失败:', e) }
      finally { this.branchLoading = false }
    },

    async submitModule() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return this.$message.warning('请完善必填信息')
        this.saving = true
        try {
          const params = {
            id: this.applyId,
            applyNo: this.applyNo,
            merchantId: this.merchantId,
            rejectReason: 'settlement',
            bankAccountType: this.localData.bankAccountType,
            accountName: this.localData.accountName,
            accountBank: this.localData.accountBank,        // 银行名称
            bankAddressCode: this.localData.bankAddressCode,
            bankBranchId: this.localData.bankBranchId,
            bankName: this.localData.bankName,
            accountNumber: this.localData.accountNumber
          }
          console.log('========== [SettlementInfo] 提交结算账户 ==========')
          console.log('提交数据:', JSON.stringify(params, null, 2))
          const res = await api.subMerchant_saveApply(params)
          console.log('[SettlementInfo] 响应:', res)
          if (res?.data?.success) {
            this.$emit('saved', res.data.data)
          } else {
            this.$message.error(res?.data?.message || '保存失败')
          }
        } catch (e) {
          console.error('[SettlementInfo] 保存失败:', e)
          this.$message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.form-tip { font-size: 12px; color: #909399; margin-top: 4px; }
.module-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; text-align: right; }
</style>